import React, { useState, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
import Box from '@mui/material/Box';

import {
    UPDATE,
    provider,
    notify
} from '../_utils';

import StatCard from './components/StatCard';
import Agents from './components/Agents';
import Utils from './utils';
import Messages from './components/Messages';
import Queues from './components/Queues';
import './dashboard.css';

// Получаем WebSocket URL на основе текущего хоста
const getWebSocketUrl = () => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname;
    if(host === 'localhost'){
        const port = process.env.REACT_APP_WS_PORT || '10012';
        return `${protocol}//${host}:${port}/ws/realtime`;
    }else{
        return `${protocol}//${host}/ws/realtime`;
    }
};

const Dashboard = () => {
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const connectionLostNotificationShown = useRef(false);
    const isMountedRef = useRef(true);
    const [data, setData] = useState({
        date: '-',
        agents: [],
        inbounds: [],
        outbounds: [],
        agentsState: {
            total: 0,
            talk: 0,
            rest: 0,
            work: 0,
            other: 0,
            notReady: 0,
            reade: 0
        },
        statistics: {
            intervals: [],
            agents: {
                occPercent: 0
            }
        }
    });

    const [serviceLevel, setServiceLevel] = useState({
        title: 'Service Lavel',
        value: '00.00%',
        trend: 'neutral',
        chip: 'AR: 0.00%',
      });
    const [taltkTime, setTalkTime] = useState( {
        title: 'Вызовов обслужено',
        value: '0',
        chip: 'Время: 00:00:00',
        trend: 'info'
    });
    const [inboundTime, setInboundTime] = useState( {
        title: 'Обслужено входящих',
        value: '0',
        secondValue: '0',
        chip: '00:00:00',
        trend: 'neutral',
    });
    const [outboundTime, setOutboundTime] = useState({
        title: 'Обслужено исходящих',
        value: '0',
        secondValue: '0',
        chip: '00:00:00',
        trend: 'neutral',
    });
    const [warnings, setWarnings] = useState({
        restOverLimit: null,
        notReadyOverTime: [],
        statusChangedToWork: [],
        operatorJoined: [],
        operatorDisconnected: [],
        events: []
    });
    // Функция для обновления статистики на основе данных из WebSocket
    const updateStatistics = useCallback((statistics) => {
        if (!statistics) return;

        const { inbound, outbound, agents, intervals } = statistics;

        // Обновляем общую статистику
        setData(prevData => ({
            ...prevData,
            statistics: {
                intervals: intervals || prevData.statistics.intervals,
                agents: agents || prevData.statistics.agents
            }
        }));

        // Обновляем Service Level и Inbound Time
        if (inbound) {
            if (inbound.sl !== undefined) {
                setServiceLevel({
                    title: 'Service Level',
                    value: `${(inbound.sl || 0).toFixed(2)}%`,
                    trend: (inbound.abandonedPercent || 0) < 3 ? 'up' : ((inbound.abandonedPercent || 0) < 4 ? 'warning' : 'down'),
                    trend2: (inbound.sl || 0) < 75 ? 'down' : ((inbound.sl || 0) < 80 ? 'warning' : 'up'),
                    chip: `AR: ${(inbound.abandonedPercent || 0).toFixed(2)}%`,
                    data: []
                });
            }

            setInboundTime({
                title: 'Обслужено входящих',
                value: `${inbound.answered || 0}`,
                secondValue: `${inbound.abandoned || 0}`,
                trend: 'neutral',
                chip: Utils.secondsToTime(inbound.talkTime || 0),
            });
        }

        // Обновляем Outbound Time
        if (outbound) {
            setOutboundTime({
                title: 'Обслужено исходящих',
                value: `${outbound.total || 0}`,
                secondValue: `${outbound.answered || 0}`,
                trend: 'neutral',
                chip: Utils.secondsToTime(outbound.talkTime || 0),
            });
        }

        if (inbound || outbound) {
            setTalkTime({
                title: 'Вызовов обслужено',
                value: `${(inbound?.answered || 0) + (outbound?.answered || 0)}`,
                trend: 'info',
                chip: `Время: ${Utils.secondsToTime((inbound?.talkTime || 0) + (outbound?.talkTime || 0))}`,
            });
        }
    }, []);

    // Подключение к WebSocket
    const connectWebSocket = useCallback(() => {
        try {
            const wsUrl = getWebSocketUrl();
            const ws = new WebSocket(wsUrl);

            ws.onopen = () => {
                // Проверяем, что компонент ещё смонтирован
                if (!isMountedRef.current) {
                    ws.close();
                    return;
                }
                
                console.log('WebSocket connected');
                
                // Сбрасываем флаг уведомления при успешном подключении
                if (connectionLostNotificationShown.current && isMountedRef.current) {
                    notify({ message: 'Соединение восстановлено', type: 'success' });
                    connectionLostNotificationShown.current = false;
                }
                
                // Подписываемся на все каналы только если компонент смонтирован
                if (isMountedRef.current) {
                ws.send(JSON.stringify({
                    type: 'subscribe',
                    channels: ['agents', 'inbounds', 'outbounds', 'statistics', 'agentsState', 'warnings']
                }));
                }
            };

            ws.onmessage = (event) => {
                // Проверяем, что компонент ещё смонтирован
                if (!isMountedRef.current) return;
                
                try {
                    const message = JSON.parse(event.data);

                    switch (message.type) {
                        case 'connected':
                            console.log('WebSocket:', message.message);
                            break;

                        case 'subscribed':
                            console.log('Subscribed to channels:', message.channels);
                            break;
                        case 'warnings':
                            if (message.data && isMountedRef.current) {
                                setWarnings(prev => {
                                    const prevEvents = prev.events || [];
                                    const incomingEvents = Array.isArray(message.data.events)
                                        ? message.data.events
                                        : [];

                                    const baseTimestamp = message.timestamp || new Date().toISOString();

                                    // Добавляем timestamp к каждому событию и накапливаем историю
                                    // Используем время события (time) для создания более точного timestamp
                                    const enriched = incomingEvents.map((e, index) => {
                                        let eventTimestamp = e.timestamp;
                                        
                                        // Если нет timestamp, но есть time, создаём timestamp из time
                                        if (!eventTimestamp && e.time) {
                                            const timeParts = e.time.split(':').map(Number);
                                            const now = moment();
                                            const eventMoment = moment(now)
                                                .hours(timeParts[0] || 0)
                                                .minutes(timeParts[1] || 0)
                                                .seconds(timeParts[2] || 0)
                                                .milliseconds(0);
                                            
                                            // Если время больше текущего, значит это было вчера
                                            if (eventMoment.isAfter(now)) {
                                                eventMoment.subtract(1, 'day');
                                            }
                                            
                                            eventTimestamp = eventMoment.toISOString();
                                        }
                                        
                                        // Если всё ещё нет timestamp, используем baseTimestamp с небольшим смещением для порядка
                                        return {
                                            ...e,
                                            timestamp: eventTimestamp || moment(baseTimestamp).add(index, 'milliseconds').toISOString()
                                        };
                                    });

                                    const mergedEvents = [...prevEvents, ...enriched];

                                    // Оставляем события только за последний час
                                    const cutoff = moment().subtract(1, 'hour');
                                    const filteredEvents = mergedEvents.filter(e => {
                                        if (!e.timestamp) return true;
                                        return moment(e.timestamp).isAfter(cutoff);
                                    });

                                    // Накапливаем notReadyOverTime события в историю (как events)
                                    const prevNotReady = prev.notReadyOverTime || [];
                                    const incomingNotReady = Array.isArray(message.data.notReadyOverTime)
                                        ? message.data.notReadyOverTime
                                        : [];
                                    
                                    // Добавляем timestamp к новым событиям notReadyOverTime
                                    const enrichedNotReady = incomingNotReady.map((a, index) => {
                                        let eventTimestamp = a.timestamp;
                                        
                                        // Если нет timestamp, но есть time, создаём timestamp из time
                                        if (!eventTimestamp && a.time) {
                                            const timeParts = a.time.split(':').map(Number);
                                            const now = moment();
                                            const eventMoment = moment(now)
                                                .hours(timeParts[0] || 0)
                                                .minutes(timeParts[1] || 0)
                                                .seconds(timeParts[2] || 0)
                                                .milliseconds(0);
                                            
                                            if (eventMoment.isAfter(now)) {
                                                eventMoment.subtract(1, 'day');
                                            }
                                            
                                            eventTimestamp = eventMoment.toISOString();
                                        }
                                        
                                        return {
                                            ...a,
                                            timestamp: eventTimestamp || moment(baseTimestamp).add(index, 'milliseconds').toISOString()
                                        };
                                    });
                                    
                                    // Объединяем: обновляем существующих по id, добавляем новых
                                    const notReadyMap = new Map();
                                    prevNotReady.forEach(a => notReadyMap.set(a.id || a.login, a));
                                    enrichedNotReady.forEach(a => notReadyMap.set(a.id || a.login, a));
                                    
                                    // Оставляем события только за последний час (используем тот же cutoff)
                                    const filteredNotReady = Array.from(notReadyMap.values()).filter(a => {
                                        if (!a.timestamp) return true;
                                        return moment(a.timestamp).isAfter(cutoff);
                                    });

                                    const nextWarnings = {
                                        ...prev,
                                        ...message.data,
                                        events: filteredEvents,
                                        notReadyOverTime: filteredNotReady
                                    };

                                    // Гарантируем timestamp для остальных типов, чтобы сортировка работала корректно
                                    if (nextWarnings.restOverLimit) {
                                        nextWarnings.restOverLimit = {
                                            ...nextWarnings.restOverLimit,
                                            timestamp: nextWarnings.restOverLimit.timestamp || baseTimestamp
                                        };
                                    }

                                    if (Array.isArray(nextWarnings.statusChangedToWork)) {
                                        nextWarnings.statusChangedToWork = nextWarnings.statusChangedToWork.map((e, index) => {
                                            let eventTimestamp = e.timestamp;
                                            
                                            // Если нет timestamp, но есть time, создаём timestamp из time
                                            if (!eventTimestamp && e.time) {
                                                const timeParts = e.time.split(':').map(Number);
                                                const now = moment();
                                                const eventMoment = moment(now)
                                                    .hours(timeParts[0] || 0)
                                                    .minutes(timeParts[1] || 0)
                                                    .seconds(timeParts[2] || 0)
                                                    .milliseconds(0);
                                                
                                                // Если время больше текущего, значит это было вчера
                                                if (eventMoment.isAfter(now)) {
                                                    eventMoment.subtract(1, 'day');
                                                }
                                                
                                                eventTimestamp = eventMoment.toISOString();
                                            }
                                            
                                            return {
                                                ...e,
                                                timestamp: eventTimestamp || moment(baseTimestamp).add(index, 'milliseconds').toISOString()
                                            };
                                        });
                                    }

                                    if (Array.isArray(nextWarnings.operatorJoined)) {
                                        nextWarnings.operatorJoined = nextWarnings.operatorJoined.map((e, index) => {
                                            let eventTimestamp = e.timestamp;
                                            
                                            // Если нет timestamp, но есть time, создаём timestamp из time
                                            if (!eventTimestamp && e.time) {
                                                const timeParts = e.time.split(':').map(Number);
                                                const now = moment();
                                                const eventMoment = moment(now)
                                                    .hours(timeParts[0] || 0)
                                                    .minutes(timeParts[1] || 0)
                                                    .seconds(timeParts[2] || 0)
                                                    .milliseconds(0);
                                                
                                                // Если время больше текущего, значит это было вчера
                                                if (eventMoment.isAfter(now)) {
                                                    eventMoment.subtract(1, 'day');
                                                }
                                                
                                                eventTimestamp = eventMoment.toISOString();
                                            }
                                            
                                            return {
                                                ...e,
                                                timestamp: eventTimestamp || moment(baseTimestamp).add(index, 'milliseconds').toISOString()
                                            };
                                        });
                                    }

                                    if (Array.isArray(nextWarnings.operatorDisconnected)) {
                                        nextWarnings.operatorDisconnected = nextWarnings.operatorDisconnected.map((e, index) => {
                                            let eventTimestamp = e.timestamp;
                                            
                                            // Если нет timestamp, но есть time, создаём timestamp из time
                                            if (!eventTimestamp && e.time) {
                                                const timeParts = e.time.split(':').map(Number);
                                                const now = moment();
                                                const eventMoment = moment(now)
                                                    .hours(timeParts[0] || 0)
                                                    .minutes(timeParts[1] || 0)
                                                    .seconds(timeParts[2] || 0)
                                                    .milliseconds(0);
                                                
                                                // Если время больше текущего, значит это было вчера
                                                if (eventMoment.isAfter(now)) {
                                                    eventMoment.subtract(1, 'day');
                                                }
                                                
                                                eventTimestamp = eventMoment.toISOString();
                                            }
                                            
                                            return {
                                                ...e,
                                                timestamp: eventTimestamp || moment(baseTimestamp).add(index, 'milliseconds').toISOString()
                                            };
                                        });
                                    }

                                    return nextWarnings;
                                });
                            }
                            break;
                        case 'agents':
                            if (isMountedRef.current) {
                            setData(prevData => ({
                                ...prevData,
                                agents: message.data || [],
                                date: message.timestamp ? moment(message.timestamp).format('DD.MM.YYYY HH:mm:ss') : moment().format('DD.MM.YYYY HH:mm:ss')
                            }));
                            }
                            break;

                        case 'inbounds':
                            if (isMountedRef.current) {
                            setData(prevData => ({
                                ...prevData,
                                inbounds: message.data || [],
                                date: message.timestamp ? moment(message.timestamp).format('DD.MM.YYYY HH:mm:ss') : moment().format('DD.MM.YYYY HH:mm:ss')
                            }));
                            }
                            break;

                        case 'outbounds':
                            if (isMountedRef.current) {
                            setData(prevData => ({
                                ...prevData,
                                outbounds: message.data || [],
                                date: message.timestamp ? moment(message.timestamp).format('DD.MM.YYYY HH:mm:ss') : moment().format('DD.MM.YYYY HH:mm:ss')

                            }));
                            }
                            break;

                        case 'statistics':
                            if (message.data && isMountedRef.current) {
                                updateStatistics(message.data);
                                // Обновляем дату при получении статистики
                                setData(prevData => ({
                                    ...prevData,
                                    date: message.timestamp ? moment(message.timestamp).format('DD.MM.YYYY HH:mm:ss') : moment().format('DD.MM.YYYY HH:mm:ss')
                                }));
                            }
                            break;

                        case 'agentsState':
                            if (isMountedRef.current) {
                            setData(prevData => ({
                                ...prevData,
                                agentsState: {
                                    ...prevData.agentsState,
                                    ...message.data
                                },
                                date: message.timestamp ? moment(message.timestamp).format('DD.MM.YYYY HH:mm:ss') : moment().format('DD.MM.YYYY HH:mm:ss')
                            }));
                            }
                            break;

                        case 'pong':
                            // Ответ на ping, ничего не делаем
                            break;

                        default:
                            console.log('Unknown message type:', message.type);
                    }
                } catch (error) {
                    console.error('Error parsing WebSocket message:', error);
                }
            };

            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
                if (!connectionLostNotificationShown.current && isMountedRef.current) {
                    notify({ message: 'Ошибка соединения WebSocket', type: 'error' });
                    connectionLostNotificationShown.current = true;
                }
            };

            ws.onclose = () => {
                console.log('WebSocket disconnected, attempting to reconnect...');
                wsRef.current = null;
                
                // Показываем уведомление о потере соединения только один раз и только если компонент смонтирован
                if (!connectionLostNotificationShown.current && isMountedRef.current) {
                    notify({ message: 'Соединение потеряно. Попытка переподключения...', type: 'warning' });
                    connectionLostNotificationShown.current = true;
                }
                
                // Переподключение через 3 секунды только если компонент ещё смонтирован
                if (isMountedRef.current) {
                reconnectTimeoutRef.current = setTimeout(() => {
                        if (isMountedRef.current) {
                    connectWebSocket();
                        }
                }, 3000);
                }
            };

            wsRef.current = ws;
        } catch (error) {
            console.error('Error creating WebSocket connection:', error);
        }
    }, [updateStatistics]);

    // Инициализация: подключение WebSocket
    useEffect(() => {
        // Устанавливаем флаг, что компонент смонтирован
        isMountedRef.current = true;
        
        // Подключаемся к WebSocket
        connectWebSocket();

        // Очистка при размонтировании
        return () => {
            // Помечаем компонент как размонтированный
            isMountedRef.current = false;
            
            // Закрываем WebSocket соединение
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            
            // Очищаем таймер переподключения
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Запускаем только один раз при монтировании

    const changeAgentState = async (agent, state) =>{
        const { label } = Utils.getAgentState(state);
        await provider(
            UPDATE,
            '/infra/agent/state',
            { 
                id: agent,
                data: { state, label }
            }
        );
    }

    return(
        <Box className="pc-page pc-dash">
            <div className="pc-dash-time">{data ? data.date : '-'}</div>
            <div className="pc-dash-kpis">
                <StatCard
                    title={'Операторы'}
                    value={data.agentsState.total.toString()}
                    trend={data.statistics.agents.occPercent<50 ? 'down' : (data.statistics.agents.occPercent<60 ? 'warning' : 'up')}
                    chip={`OCC: ${data.statistics.agents.occPercent.toFixed(2)}%`}
                    agentsStates={data.agentsState}
                />
                <StatCard {...serviceLevel} data={data.statistics.intervals} dataParam={'sl'}/>
                <StatCard {...taltkTime} data={data.statistics.intervals} dataParam={'answered'}/>
            </div>
            <div className="pc-dash-body">
                <div className="pc-dash-col">
                    {data &&
                        <Agents agents={data.agents} talk={data.agentsState.talk} changeAgentState={changeAgentState}/>
                    }
                </div>
                <div className="pc-dash-col">
                    {data &&
                        <Queues queues={data} inboundTime={inboundTime} outboundTime={outboundTime}/>
                    }
                </div>
                <div className="pc-dash-col pc-dash-col-events">
                    <Messages warnings={warnings} />
                </div>
            </div>
        </Box>
    )
}

export default Dashboard;